import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X, ArrowRight } from "lucide-react";
import { NAV_LINKS, SITE, LOGO } from "@/content/site";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 ${
        scrolled || open
          ? "bg-white/85 backdrop-blur-xl border-b border-slate-200/70 shadow-[0_8px_30px_rgba(10,25,47,0.06)]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto">
        <div className="flex items-center justify-between h-[72px]">
          <Link to="/" data-testid="header-logo-link" className="flex items-center gap-3 mr-4 group">
            <img
              src={LOGO.chip}
              alt="Point of Pines Pools & Service logo"
              data-testid="header-logo-image"
              className="w-11 h-11 rounded-full shadow-md transition-transform duration-300 group-hover:scale-105"
            />
            <span className="leading-tight">
              <span className={`block whitespace-nowrap font-display font-extrabold tracking-tight text-[17px] ${scrolled || open ? "text-navy" : "text-white"}`}>
                Point of Pines
              </span>
              <span className={`block whitespace-nowrap text-[11px] font-mono uppercase tracking-[0.22em] ${scrolled || open ? "text-pool" : "text-sky2"}`}>
                Pools &amp; Service
              </span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-4 xl:gap-5" aria-label="Primary">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.path}
                to={l.path}
                data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}-link`}
                className={({ isActive }) =>
                  `whitespace-nowrap text-[13px] xl:text-sm font-medium transition-colors duration-200 ${
                    scrolled
                      ? isActive ? "text-pool" : "text-slate-600 hover:text-navy"
                      : isActive ? "text-sky2" : "text-white/80 hover:text-white"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href={SITE.phoneHref}
              data-testid="header-phone-link"
              className={`hidden xl:flex items-center gap-2 whitespace-nowrap text-sm font-semibold px-4 py-2.5 rounded-full transition-colors duration-200 ${
                scrolled ? "text-navy hover:bg-mist" : "text-white hover:bg-white/10"
              }`}
            >
              <Phone className="w-4 h-4" />
              {SITE.phone}
            </a>
            <Link
              to="/contact/"
              data-testid="header-request-quote-button"
              className="flex items-center gap-2 whitespace-nowrap bg-aqua text-navy text-sm font-bold px-5 py-2.5 rounded-full transition-all duration-300 hover:bg-sky2 hover:text-navy hover:shadow-lg hover:shadow-cyan-500/30 hover:-translate-y-0.5"
            >
              Request a Quote
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            className={`lg:hidden w-11 h-11 flex items-center justify-center rounded-xl transition-colors ${scrolled || open ? "text-navy hover:bg-mist" : "text-white hover:bg-white/10"}`}
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-t border-slate-200/60"
            aria-label="Mobile"
          >
            <div className="px-4 py-4 flex flex-col gap-1">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.path}
                  to={l.path}
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}-link`}
                  className={({ isActive }) =>
                    `px-4 py-3.5 rounded-xl text-base font-semibold transition-colors ${
                      isActive ? "bg-mist text-pool" : "text-navy hover:bg-mist"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <div className="grid grid-cols-2 gap-3 pt-3 pb-2">
                <a
                  href={SITE.phoneHref}
                  data-testid="mobile-call-button"
                  className="flex items-center justify-center gap-2 border-2 border-navy text-navy font-bold py-3.5 rounded-full text-sm"
                >
                  <Phone className="w-4 h-4" /> Call Now
                </a>
                <Link
                  to="/contact/"
                  data-testid="mobile-quote-button"
                  className="flex items-center justify-center gap-2 bg-aqua text-navy font-bold py-3.5 rounded-full text-sm"
                >
                  Get a Quote
                </Link>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
